import { baseApi } from "@/config/http-common";
import { filemanagerApi } from "@/config/http-filemanager";

class DocumentDataService {
  getAll(filter: any): Promise<any> {
    return baseApi.get("/documents/document/list", { params: filter });
  }

  get(id: any): Promise<any> {
    return baseApi.get(`/documents/document/view/${id}`);
  }

  create(data: any): Promise<any> {
    return baseApi.post("/documents/document/create", data);
  }

  update(id: any, data: any): Promise<any> {
    return baseApi.put(`/documents/document/update/${id}`, data);
  }

  delete(id: string): Promise<any> {
    return baseApi.delete(`/documents/document/delete/${id}`);
  }

  getConstructors(params?: { page?: number }): Promise<any> {
    return baseApi.get("/documents/document/sub-data/constructors", { params });
  }

  getConstructorInfo(id: any): Promise<any> {
    return baseApi.get("/documents/document/sub-data/constructor-info", {
      params: { id: id },
    });
  }

  getNomenclatures(params?: { page?: number }): Promise<any> {
    return baseApi.get("/documents/document/sub-data/nomenclatures", {
      params,
    });
  }

  getProcessUserFilter(data: any): Promise<any> {
    return baseApi.post(
      "/documents/document/sub-data/process-user-filter",
      data
    );
  }

  getOneDocumentUpdateInfo(id: any): Promise<any> {
    return baseApi.get("/documents/document/sub-data/document-update-info", {
      params: { id: id },
    });
  }

  uploadDocument(file: any): Promise<any> {
    return filemanagerApi.post("/upload-document", file, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  getFilesInfo(file: any): Promise<any> {
    return filemanagerApi.post("/files-info", file);
  }

  returnDocument(id: any): Promise<any> {
    return baseApi.get(`/documents/document/return/${id}`);
  }

  downloadDocument(id: any, url: any): Promise<any> {
    const params: Record<string, any> = { file: id };
    if (url) {
      params.url = url;
    }
    return filemanagerApi.get("/download", {
      params,
      responseType: "blob",
    });
  }

  getPublicSignsUrl(documentId: string): Promise<any> {
    return baseApi.get(`/documents/document/${documentId}/public-signs-url`, {
      silent: true,
    } as any);
  }

  downloadDocumentWithInfo(id: any): Promise<any> {
    return filemanagerApi.get("/download-with-info", {
      params: { file: id },
      responseType: "blob",
    });
  }

  sendForExecution(id: any): Promise<any> {
    return baseApi.get(`/documents/document/send-for-execution/${id}`);
  }

  getConstructorFilter(): Promise<any> {
    return baseApi.get(`/documents/document/filter-data/constructors`);
  }

  getCreatorFilter(params: { page?: number; fullName?: string }): Promise<any> {
    return baseApi.get(`/documents/document/filter-data/creators`, { params });
  }

  generateArchive(id: string): Promise<any> {
    return baseApi.get(`/documents/document/get-archive`, {
      params: {
        id: id,
      },
    });
  }

  downloadElectronicCopies(id: string): Promise<any> {
    return baseApi.get(`/documents/document/electronic-copies`, {
      params: {
        id: id,
      },
    });
  }

  getElectronicCopyFiles(id: string): Promise<any> {
    return baseApi.get(`/documents/document/electronic-copy-files`, {
      params: {
        id: id,
      },
      silent: true,
    } as any);
  }

  addCoEditor(documentId: string, userId: string): Promise<any> {
    return baseApi.post(`/documents/document/add/co-editor-access/${documentId}`, {
      userId: userId,
    });
  }

  addViewAccess(documentId: string, userId: string): Promise<any> {
    return baseApi.post(`/documents/document/add/access/${documentId}`, {
      userId: userId,
    });
  }

  deleteCoEditor(documentId: string, userId: string): Promise<any> {
    return baseApi.post(`/documents/document/delete/access/${documentId}`, {
      userId: userId,
    });
  }

  getSignInfo(
    documentId: string,
    fileId: string | null,
    processUserId: string
  ): Promise<any> {
    return baseApi.post(`documents/document/sign-info/${documentId}`, {
      processUserId: processUserId,
      fileId: fileId,
    });
  }
}

export default new DocumentDataService();
